import { useEffect, useRef } from 'react';

/**
 * Swipe-down-to-dismiss for bottom sheets on touch screens. Attach the returned ref to the
 * sheet element. The drag only engages on the phone layout (sheet anchored to the bottom),
 * when the sheet is scrolled to the top, when the gesture didn't start on a text-entry
 * control, and once the gesture is clearly downward — so it never fights content scrolling,
 * horizontal swipes over the chips, or typing.
 */
export function useSheetDrag(onClose: () => void) {
  const ref = useRef<HTMLDivElement>(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const SLOP = 12; // px of downward travel before the drag engages
    const DISMISS = 100; // px beyond which release dismisses
    const FLICK = 0.5; // px/ms — a short fast flick also dismisses

    // Text-entry controls are never a drag surface. On iOS a preventDefault()ed touchmove
    // cancels the synthesised click, so a slightly-smudged tap on the notes box engaged the
    // sheet drag instead of focusing the field — the keyboard never appeared and comments
    // couldn't be typed. Placing a cursor or selecting text is a downward drag too, so the
    // sheet must keep its hands off entirely. Buttons/links stay draggable: a real tap on one
    // doesn't travel SLOP px, and a tap that DOES drift ought to be cancelled.
    const TEXT_ENTRY = 'input, textarea, select, [contenteditable]';

    let startY = 0;
    let startX = 0;
    let startT = 0;
    let dy = 0;
    let eligible = false;
    let dragging = false;
    let closing = false;
    let closeTimer: number | undefined;

    const settleBack = () => {
      el.style.transition = 'transform 180ms ease-out';
      el.style.transform = '';
      window.setTimeout(() => {
        el.style.transition = '';
      }, 200);
    };

    const onStart = (e: TouchEvent) => {
      if (closing || e.touches.length !== 1) return;
      // ≥769px centres the dialog and hides the grab handle — the gesture is phone-only.
      // scrollTop ≤ 0 so a swipe that's really "scroll back up the sheet" isn't hijacked.
      const target = e.target instanceof Element ? e.target : null;
      eligible =
        window.matchMedia('(max-width: 768px)').matches &&
        el.scrollTop <= 0 &&
        target?.closest(TEXT_ENTRY) == null;
      startY = e.touches[0].clientY;
      startX = e.touches[0].clientX;
      startT = e.timeStamp;
      dy = 0;
      dragging = false;
    };

    const onMove = (e: TouchEvent) => {
      if (closing || !eligible) return;
      const t = e.touches[0];
      dy = t.clientY - startY;
      if (!dragging) {
        if (dy < SLOP || Math.abs(t.clientX - startX) > dy) return;
        dragging = true;
      }
      e.preventDefault(); // the sheet owns the gesture now — stop content scroll under it
      el.style.transition = 'none';
      el.style.transform = `translateY(${Math.max(0, dy)}px)`;
    };

    const onEnd = (e: TouchEvent) => {
      if (!dragging || closing) return;
      dragging = false;
      const flick = dy / Math.max(1, e.timeStamp - startT);
      if (dy > DISMISS || flick > FLICK) {
        closing = true;
        el.style.transition = 'transform 160ms ease-in';
        el.style.transform = 'translateY(100%)';
        closeTimer = window.setTimeout(() => closeRef.current(), 160);
      } else {
        settleBack();
      }
    };

    const onCancel = () => {
      if (closing || !dragging) return;
      dragging = false;
      settleBack();
    };

    el.addEventListener('touchstart', onStart, { passive: true });
    el.addEventListener('touchmove', onMove, { passive: false });
    el.addEventListener('touchend', onEnd);
    el.addEventListener('touchcancel', onCancel);
    return () => {
      window.clearTimeout(closeTimer);
      el.removeEventListener('touchstart', onStart);
      el.removeEventListener('touchmove', onMove);
      el.removeEventListener('touchend', onEnd);
      el.removeEventListener('touchcancel', onCancel);
    };
  }, []);

  return ref;
}
